/**
 * ProviderPluckonomicsPage — PooPartner Pluckonomics tab.
 *
 * Mounted at `/dashboard/provider/pluckonomics` inside the ProviderShell.
 *
 * Layout (mirroring the brief's mockup spec — KPI row + premium grid + centered overlay CTA):
 *   1. Three FREE KPI cards (`realized_earnings`, `projected_12mo`,
 *      `unearned`) — always rendered clear, payout-framed (partner
 *      TAKE only, never commission / processing fee / GMV).
 *   2. Premium grid (six cards: trend / revenue-vs-goal / goal-progress
 *      / break-even / earnings-statement / export). When the partner
 *      is NOT entitled, the grid renders blurred silhouettes AND is
 *      `aria-hidden="true"` so screen readers don't read the fake
 *      placeholder numbers as real data.
 *   3. Centered overlay card on top of the blurred area with the
 *      TIER-AWARE intent-capture CTA (2026-07-24 rework). The gate
 *      renders differently based on the viewer's current tier:
 *        - Standard viewer → "Save my spot" records interest in
 *          Preferred (target tier the gate unlocks at).
 *        - Preferred viewer + Elite-only gate → records Elite.
 *        - Elite viewer → the overlay card is hidden entirely (no
 *          next rung to sell).
 *      Affirmative price disclosure ("Preferred — $99/mo when
 *      billing launches. No charge today; nothing starts
 *      automatically.") renders BEFORE the "Save my spot" button
 *      is actionable. On click, POST /api/partner/tier/select with
 *      `tier_id: <target>` (per the locked `tier_selection.py`
 *      contract). Zero billing consequence, zero entitlement grant.
 *      Confirmation copy (verbatim): "Saved — we'll email you
 *      before billing launches. You'll confirm your plan then;
 *      nothing is charged or auto-started." NEVER records
 *      `tier=standard` — that's the viewer's current tier, not
 *      something to reserve. See `/app/memory/PRD.md` — "Intent
 *      Capture — Affirmative Price Disclosure" subsection.
 *      When the partner IS entitled (existing legacy grants from
 *      pre-Dispatch-#5 preview period), the overlay is absent and
 *      the grid renders clear.
 *
 * Brand palette per brief: brand orange `#F89623` (variables ->
 * `--pp-orange`), slate `#374751` (`--pp-text`), purple `#574B8C`
 * (used for the premium accent). Background cool grey `#EEF1F3`
 * matches the page surface.
 *
 * Encouragement framing (HARD per the brief):
 *   - Goal copy: "70% there — $X to go" (NEVER "30% short of goal").
 *   - Trend deltas: negative deltas render in NEUTRAL slate, NEVER
 *     red, NEVER with a down-arrow. Positive deltas use brand orange.
 *   - Banned strings ("behind", "short of goal", "below") and unicode
 *     down-arrows (▼ ↓ ⬇) appear NOWHERE.
 *
 * Config-driven gate (HARD per the brief): the free/premium split
 * comes from the backend (`card_tiers` in the response). Admin can
 * flip a card to "free" via `PATCH /api/config/pricing` and this page
 * shows it clear without a code change.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles,
  TrendingUp,
  Target,
  ArrowUpRight,
  FileText,
  Download,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";

const PREMIUM_CARDS_ORDER = [
  "trend_wow_mom",
  "revenue_vs_goal",
  "goal_progress",
  "break_even",
  "earnings_statement",
  "export",
];

const CARD_LABELS = {
  realized_earnings: "Realized earnings",
  projected_12mo: "Projected (12-mo)",
  unearned: "Unearned (in flight)",
  trend_wow_mom: "Trend",
  revenue_vs_goal: "Revenue vs goal",
  goal_progress: "Goal progress",
  break_even: "Break-even tool",
  earnings_statement: "Earnings statement",
  export: "Export",
};

function fmtUsd(cents) {
  const n = Number(cents || 0) / 100;
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function KpiCard({ id, label, valueCents }) {
  return (
    <Card
      className="rounded-3xl border-pp-border p-6"
      data-testid={`pluckonomics-kpi-${id}`}
    >
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p
        className="mt-2 font-heading text-3xl font-bold text-pp-text sm:text-4xl"
        data-testid={`pluckonomics-kpi-${id}-value`}
      >
        {fmtUsd(valueCents)}
      </p>
    </Card>
  );
}

function TrendBadge({ pct }) {
  // Trend pct can be negative — we render the magnitude in NEUTRAL
  // slate (NOT red), with a "+" prefix for positive only. No down-
  // arrow icon for negative deltas. Brief invariant: "negative deltas
  // render in NEUTRAL color (slate grey), NEVER red".
  const positive = pct > 0;
  const sign = positive ? "+" : "";
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold",
        positive
          ? "bg-pp-orange/15 text-pp-text"
          : "bg-muted text-pp-text",
      ].join(" ")}
      data-testid="pluckonomics-trend-badge"
    >
      {positive ? (
        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
      ) : null}
      {sign}{Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function GoalProgressContent({ value }) {
  const v = value || {};
  if (!v.has_goal) {
    return (
      <div data-testid="pluckonomics-goal-no-target">
        <p className="text-sm font-semibold text-pp-text">
          Set your monthly earnings goal
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          We&rsquo;ll show your progress and what&rsquo;s ahead.
        </p>
      </div>
    );
  }
  const pct = v.pct || 0;
  return (
    <div data-testid="pluckonomics-goal-progress">
      <p
        className="text-sm font-semibold text-pp-text"
        data-testid="pluckonomics-goal-progress-text"
      >
        {pct}% there
        {v.to_go_cents > 0 ? (
          <span className="font-normal text-muted-foreground">
            {" "}
            &mdash; {fmtUsd(v.to_go_cents)} to go
          </span>
        ) : null}
      </p>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-pp-orange transition-all"
          style={{ width: `${Math.min(100, pct)}%` }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

function PremiumCardShell({ id, label, locked, children }) {
  const Icon = {
    trend_wow_mom: TrendingUp,
    revenue_vs_goal: TrendingUp,
    goal_progress: Target,
    break_even: TrendingUp,
    earnings_statement: FileText,
    export: Download,
  }[id] || TrendingUp;
  return (
    <Card
      className="rounded-3xl border-pp-border p-5"
      data-testid={`pluckonomics-premium-${id}`}
      data-locked={locked ? "true" : "false"}
    >
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-pp-text" aria-hidden="true" />
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
      </div>
      {children}
    </Card>
  );
}

function PremiumCardContent({ card }) {
  if (card.locked) {
    // Structural placeholder — NO real data. The blur on the parent
    // container hides this fully; what's important is the SHAPE so
    // the silhouette feels real. We deliberately use a generic
    // string (no real number) here so even if the blur were removed
    // by accident the partner wouldn't see fake metrics.
    return (
      <div className="space-y-2">
        <div className="h-7 w-24 rounded bg-muted" />
        <div className="h-3 w-32 rounded bg-muted" />
        <div className="h-12 w-full rounded bg-muted" />
      </div>
    );
  }
  // Unlocked rendering for entitled partners. Item L (2026-06-29)
  // wired the three premium widgets (statement / break-even / export)
  // through dedicated detail endpoints — the values surfaced on the
  // main `/partner/pluckonomics` payload now carry `endpoint` hints
  // that the entitled-only sub-components use to fetch additional
  // data on demand.
  const id = card.id;
  if (id === "trend_wow_mom") {
    const v = card.value || {};
    return (
      <div className="flex items-center gap-3" data-testid="pluckonomics-trend-content">
        <TrendBadge pct={Number(v.mom_pct || 0)} />
        <span className="text-xs text-muted-foreground">vs prior 30 days</span>
      </div>
    );
  }
  if (id === "revenue_vs_goal") {
    const v = card.value || {};
    return (
      <div data-testid="pluckonomics-revenue-vs-goal-content">
        <p className="text-2xl font-bold text-pp-text">
          {fmtUsd(v.trailing_30d_cents)}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {v.goal_monthly_cents
            ? `Goal: ${fmtUsd(v.goal_monthly_cents)} / mo`
            : "Set a goal to enable this view"}
        </p>
      </div>
    );
  }
  if (id === "goal_progress") {
    return <GoalProgressContent value={card.value} />;
  }
  if (id === "break_even") {
    return <BreakEvenCardContent />;
  }
  if (id === "earnings_statement") {
    return <EarningsStatementCardContent />;
  }
  if (id === "export") {
    return <ExportCardContent />;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Item L — entitled-only premium widget sub-components.
//
// Each sub-component fetches its dedicated detail endpoint when the
// partner expands the card. Free / locked partners never reach this
// branch (the parent `PremiumCardContent` short-circuits on
// `card.locked === true` and renders the silhouette instead).
//
// Encouragement framing: negative net / missed break-even are surfaced
// in NEUTRAL slate copy, never red, never with "behind" / "short" /
// "below" framing.
// ---------------------------------------------------------------------------

function fmtUsdPrecise(cents) {
  const n = Number(cents || 0) / 100;
  // Render the sign OUTSIDE the dollar prefix so a negative net displays
  // as `-$752.00` (conventional) instead of `$-752.00`.
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  return `${sign}$${abs.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function BreakEvenCardContent() {
  // Inline-expand card. Collapsed by default to keep the premium
  // grid scannable; "Edit costs" opens the input panel.
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fuel, setFuel] = useState("");
  const [supplies, setSupplies] = useState("");
  const [labor, setLabor] = useState("");
  const [fixed, setFixed] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/pluckonomics/break-even");
      setData(data);
      const inp = data?.inputs || {};
      // Convert persisted cents → dollars for the input fields (the
      // partner thinks in dollars; we round-trip back to cents on save).
      setFuel(centsToDollars(inp.fuel_cents_per_booking));
      setSupplies(centsToDollars(inp.supplies_cents_per_booking));
      setLabor(centsToDollars(inp.labor_cents_per_booking));
      setFixed(centsToDollars(inp.fixed_overhead_cents_per_month));
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || e.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const onToggle = async () => {
    if (!open && !data) {
      await load();
    }
    setOpen((v) => !v);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const body = {
        fuel_cents_per_booking: dollarsToCents(fuel),
        supplies_cents_per_booking: dollarsToCents(supplies),
        labor_cents_per_booking: dollarsToCents(labor),
        fixed_overhead_cents_per_month: dollarsToCents(fixed),
      };
      const { data: next } = await api.put(
        "/partner/pluckonomics/break-even",
        body,
      );
      setData(next);
      toast.success("Break-even costs saved.");
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || e.message,
      );
    } finally {
      setSaving(false);
    }
  };

  const computed = data?.computed || {};
  const beBookings = computed.break_even_bookings_per_month;
  const actualBookings = computed.actual_bookings_per_month || 0;
  // Encouragement-framed: if past break-even, celebrate; otherwise,
  // show how many more bookings would cover overhead. Never "$X behind".
  let beCopy = null;
  if (beBookings === null || beBookings === undefined) {
    beCopy = "Raise your per-booking payout or trim variable cost to enable break-even.";
  } else if (beBookings === 0) {
    beCopy = "No fixed overhead — every booking contributes to net.";
  } else if (actualBookings >= beBookings) {
    beCopy = `You're past break-even — ${
      actualBookings - beBookings
    } booking(s) ahead of overhead this month.`;
  } else {
    const moreToGo = beBookings - actualBookings;
    beCopy = `${moreToGo} more booking${
      moreToGo === 1 ? "" : "s"
    } this month covers your overhead.`;
  }

  return (
    <div data-testid="pluckonomics-break-even-content">
      <Button
        size="sm"
        variant="outline"
        onClick={onToggle}
        data-testid="pluckonomics-break-even-toggle"
        className="mb-2"
      >
        {loading ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />
        ) : open ? (
          <ChevronUp className="mr-1 h-3 w-3" aria-hidden="true" />
        ) : (
          <ChevronDown className="mr-1 h-3 w-3" aria-hidden="true" />
        )}
        {open ? "Hide" : "Edit costs"}
      </Button>
      {data && !open ? (
        // Collapsed summary — quick read of the computed break-even.
        <div className="space-y-1 text-sm" data-testid="pluckonomics-break-even-summary">
          <p className="font-semibold text-pp-text">
            {beBookings === null || beBookings === undefined
              ? "—"
              : `${beBookings} bookings / month`}
          </p>
          <p className="text-xs text-muted-foreground">{beCopy}</p>
        </div>
      ) : null}
      {open ? (
        <div
          className="mt-3 space-y-3"
          data-testid="pluckonomics-break-even-expanded"
        >
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="be-fuel" className="text-xs">Fuel ($/booking)</Label>
              <Input
                id="be-fuel"
                inputMode="decimal"
                value={fuel}
                onChange={(e) => setFuel(e.target.value)}
                data-testid="pluckonomics-break-even-input-fuel"
              />
            </div>
            <div>
              <Label htmlFor="be-supplies" className="text-xs">Supplies ($/booking)</Label>
              <Input
                id="be-supplies"
                inputMode="decimal"
                value={supplies}
                onChange={(e) => setSupplies(e.target.value)}
                data-testid="pluckonomics-break-even-input-supplies"
              />
            </div>
            <div>
              <Label htmlFor="be-labor" className="text-xs">Labor ($/booking)</Label>
              <Input
                id="be-labor"
                inputMode="decimal"
                value={labor}
                onChange={(e) => setLabor(e.target.value)}
                data-testid="pluckonomics-break-even-input-labor"
              />
            </div>
            <div>
              <Label htmlFor="be-fixed" className="text-xs">Fixed overhead ($/mo)</Label>
              <Input
                id="be-fixed"
                inputMode="decimal"
                value={fixed}
                onChange={(e) => setFixed(e.target.value)}
                data-testid="pluckonomics-break-even-input-fixed"
              />
            </div>
          </div>
          <Button
            size="sm"
            onClick={onSave}
            disabled={saving}
            className="w-full rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
            data-testid="pluckonomics-break-even-save"
          >
            {saving ? "Saving…" : "Save costs"}
          </Button>
          {data ? (
            <div
              className="rounded-2xl border border-pp-border bg-background p-3 text-xs space-y-1"
              data-testid="pluckonomics-break-even-computed"
            >
              <p>
                <span className="font-semibold">Break-even:</span>{" "}
                <span data-testid="pluckonomics-break-even-volume">
                  {beBookings === null || beBookings === undefined
                    ? "—"
                    : `${beBookings} bookings / mo`}
                </span>
              </p>
              <p>
                <span className="font-semibold">Trailing 30d net:</span>{" "}
                <span data-testid="pluckonomics-break-even-net">
                  {fmtUsdPrecise(computed.net_30d_cents)}
                </span>
              </p>
              <p>
                <span className="font-semibold">Avg payout / booking:</span>{" "}
                {fmtUsdPrecise(computed.avg_payout_per_booking_cents)}
              </p>
              <p>
                <span className="font-semibold">Variable cost / booking:</span>{" "}
                {fmtUsdPrecise(computed.variable_cost_per_booking_cents)}
              </p>
              <p className="text-muted-foreground">{beCopy}</p>
            </div>
          ) : null}
        </div>
      ) : null}
      {!data && !open && !loading ? (
        <p className="text-xs text-muted-foreground">
          Enter your costs to compute break-even volume and trailing-30d net.
        </p>
      ) : null}
    </div>
  );
}

function EarningsStatementCardContent() {
  const [open, setOpen] = useState(false);
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);

  const onToggle = async () => {
    if (!open && !statement) {
      setLoading(true);
      try {
        const { data } = await api.get("/partner/pluckonomics/statement");
        setStatement(data);
      } catch (e) {
        toast.error(
          formatApiErrorDetail(e?.response?.data?.detail) || e.message,
        );
        return;
      } finally {
        setLoading(false);
      }
    }
    setOpen((v) => !v);
  };

  return (
    <div data-testid="pluckonomics-earnings_statement-content">
      <Button
        size="sm"
        variant="outline"
        onClick={onToggle}
        data-testid="pluckonomics-statement-toggle"
        className="mb-2"
      >
        {loading ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />
        ) : open ? (
          <ChevronUp className="mr-1 h-3 w-3" aria-hidden="true" />
        ) : (
          <ChevronDown className="mr-1 h-3 w-3" aria-hidden="true" />
        )}
        {open ? "Hide statement" : "View statement"}
      </Button>
      {open && statement ? (
        <div
          className="rounded-2xl border border-pp-border bg-background p-3 text-xs"
          data-testid="pluckonomics-statement-detail"
        >
          <p className="font-semibold text-pp-text">
            {statement.period_label}
          </p>
          <p className="text-muted-foreground">
            {statement.bookings_count} bookings (last 30d)
          </p>
          <div className="mt-2 space-y-1">
            {(statement.line_items || []).map((li) => (
              <div
                key={li.id}
                className="flex items-center justify-between gap-2"
                data-testid={`pluckonomics-statement-line-${li.id}`}
              >
                <span>{li.label}</span>
                <span className="font-mono">{fmtUsdPrecise(li.amount_cents)}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground">
            All amounts are partner payouts.
          </p>
        </div>
      ) : null}
      {!open ? (
        <p className="text-xs text-muted-foreground">
          Itemized payout-framed earnings for the current period.
        </p>
      ) : null}
    </div>
  );
}

function ExportCardContent() {
  const [busy, setBusy] = useState(null); // "pdf" | "xlsx" | null

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.get(
        `/partner/pluckonomics/statement/export?format=${format}`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], {
        type:
          res.headers?.["content-type"] ||
          (format === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"),
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pluckonomics-statement.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(
        `Statement exported as ${format === "pdf" ? "PDF" : "Excel"}.`,
      );
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || e.message,
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="pluckonomics-export-content"
    >
      <Button
        size="sm"
        variant="outline"
        onClick={() => download("pdf")}
        disabled={busy !== null}
        data-testid="pluckonomics-export-pdf"
      >
        {busy === "pdf" ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />
        ) : (
          <FileText className="mr-1 h-3 w-3" aria-hidden="true" />
        )}
        Download PDF
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => download("xlsx")}
        disabled={busy !== null}
        data-testid="pluckonomics-export-xlsx"
      >
        {busy === "xlsx" ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />
        ) : (
          <Download className="mr-1 h-3 w-3" aria-hidden="true" />
        )}
        Download Excel
      </Button>
      <p className="text-[10px] text-muted-foreground">
        Payout-framed only. Same numbers as the statement above.
      </p>
    </div>
  );
}

function centsToDollars(cents) {
  if (cents === null || cents === undefined) return "";
  const n = Number(cents) || 0;
  if (n === 0) return "";
  return (n / 100).toFixed(2);
}

function dollarsToCents(dollars) {
  const s = String(dollars || "").trim();
  if (!s) return 0;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

export default function ProviderPluckonomicsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startingTrial, setStartingTrial] = useState(false);
  // Session-local confirmation: flips to `true` once the partner has
  // successfully recorded intent for the Standard tier via
  // `/partner/tier/select`. Persistence across reloads is not needed
  // — the intent record on the backend is idempotent, and a returning
  // partner can safely re-click.
  const [interestRecorded, setInterestRecorded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/partner/pluckonomics");
      setData(data);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || e.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSaveMySpot = async (targetTier) => {
    setStartingTrial(true);
    try {
      // Intent-only tier selection. Writes `users.selected_partner_tier`
      // per `tier_selection.py`; grants NO entitlement, touches NO
      // Stripe surface. Session-local `interestRecorded` state drives
      // the confirmation UI — the dashboard payload doesn't return
      // `selected_partner_tier`, so a page reload re-shows the card.
      // Idempotent on the backend: re-clicking updates
      // `selected_partner_tier_at` and is safe.
      //
      // TIER-AWARE (Item 3 follow-up, 2026-07-24): NEVER records
      // `tier=standard` from this upsell gate — the viewer is a
      // Standard-or-Preferred partner asking to be notified when
      // billing launches for a HIGHER tier. See PRD "Intent Capture
      // — Affirmative Price Disclosure" (memory/PRD.md).
      await api.post("/partner/tier/select", { tier_id: targetTier });
      setInterestRecorded(true);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || e.message,
      );
    } finally {
      setStartingTrial(false);
    }
  };

  const entitled = data?.entitlement?.entitled === true;
  const trialUsedUp = data?.entitlement?.trial_used_up === true;
  // TIER-AWARE upsell (Item 3 follow-up, 2026-07-24).
  //
  // `currentTierId` — the viewer's tier as of this dashboard load.
  //   Falls back to `"standard"` if missing (matches backend default).
  //   Elite viewers see NO upsell card — no next rung to sell.
  //
  // `minGateTier` — which tier the CURRENT gate requires. The
  //   Pluckonomics premium grid unlocks at Preferred+, so the
  //   default gate tier is `"preferred"`. Reserved for future
  //   Elite-only gates via a prop / card_tiers config field.
  //
  // `targetTier` — what the "Save my spot" button records:
  //   * Standard viewer + Preferred+ gate → "preferred"
  //   * Preferred viewer + Elite-only gate → "elite"
  //   * Elite viewer → card hidden entirely (returns null below)
  //
  // NEVER records `tier=standard` from this upsell gate. The viewer
  // IS Standard (that's why they see the card) — reserving their
  // current tier is meaningless.
  const currentTierId = data?.current_tier_id || "standard";
  const tierPricing = data?.tier_pricing || {};
  const minGateTier = "preferred";
  const showUpsellCard = currentTierId !== "elite" && !entitled;
  const targetTier =
    currentTierId === "standard" ? minGateTier
    : currentTierId === "preferred" ? "elite"
    : null;
  const targetTierLabel = targetTier === "preferred" ? "Preferred"
    : targetTier === "elite" ? "Elite" : "";
  const targetTierPriceCents = tierPricing[targetTier];
  const targetTierPriceDollars =
    Number.isFinite(targetTierPriceCents) && targetTierPriceCents > 0
      ? Math.round(targetTierPriceCents / 100)
      : null;
  // `cards = data?.cards || {}` synthesizes a fresh `{}` identity on
  // every render when data is unloaded; the useMemo below (kpiRow, L704)
  // would then recompute pointlessly. Not a user-visible defect —
  // KpiCard tree is cheap and reconciles by `key`; wrapping `cards` in
  // its own useMemo would add a memo layer for no observable win.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cards = data?.cards || {};

  // Three free KPI cards — always clear regardless of entitlement.
  const kpiRow = useMemo(() => {
    return ["realized_earnings", "projected_12mo", "unearned"].map((id) => {
      const c = cards[id];
      return (
        <KpiCard
          key={id}
          id={id}
          label={CARD_LABELS[id]}
          valueCents={c?.value || 0}
        />
      );
    });
  }, [cards]);

  if (loading) {
    return (
      <div
        className="mx-auto max-w-6xl px-2 py-8 text-sm text-muted-foreground"
        data-testid="pluckonomics-loading"
      >
        Loading your Pluckonomics&hellip;
      </div>
    );
  }

  // Build the premium grid — note: cards whose tier is "free" via
  // admin config render CLEAR even for unentitled partners. Cards
  // whose tier is "premium" render locked iff !entitled.
  // The blur lives on a wrapper that covers ALL premium-tier cards;
  // free-tier cards in the premium-grid position render outside the
  // blur block.
  const premiumGridCards = PREMIUM_CARDS_ORDER.map((id) => cards[id]).filter(Boolean);
  const anyLocked = premiumGridCards.some((c) => c.locked);

  return (
    <div className="mx-auto max-w-6xl px-2 py-8" data-testid="pluckonomics-page">
      <header className="mb-6">
        <span className="text-xs font-bold uppercase tracking-wider text-brand-orange">
          Pluckonomics
        </span>
        <h1 className="font-heading text-3xl font-bold text-pp-text sm:text-4xl">
          Your earnings, end-to-end
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Realized, projected, and in-flight &mdash; payout-framed.
        </p>
      </header>

      {/* Free KPI row — always clear */}
      <div
        className="mb-8 grid gap-4 sm:grid-cols-3"
        data-testid="pluckonomics-free-kpi-row"
      >
        {kpiRow}
      </div>

      {/* Premium grid (with overlay CTA if not entitled) */}
      <section className="relative" data-testid="pluckonomics-premium-section">
        <div
          className={[
            "grid gap-4 sm:grid-cols-2 lg:grid-cols-3 transition-all",
            anyLocked && !entitled ? "blur-sm pointer-events-none select-none" : "",
          ].join(" ")}
          // Accessibility — when blurred, the premium grid is
          // hidden from screen readers so assistive tech doesn't
          // read out the placeholder silhouettes as real data.
          // The overlay CTA below IS in the a11y tree and serves
          // as the screen-reader-visible content for this region.
          aria-hidden={anyLocked && !entitled ? "true" : undefined}
          data-testid="pluckonomics-premium-grid"
        >
          {premiumGridCards.map((c) => (
            <PremiumCardShell
              key={c.id}
              id={c.id}
              label={CARD_LABELS[c.id]}
              locked={c.locked}
            >
              <PremiumCardContent card={c} />
            </PremiumCardShell>
          ))}
        </div>

        {anyLocked && showUpsellCard ? (
          <div
            className="absolute inset-0 flex items-center justify-center p-4"
            data-testid="pluckonomics-overlay"
          >
            <div
              className="max-w-md rounded-3xl border border-pp-border bg-pp-surface p-8 text-center shadow-2xl"
              style={{ borderColor: "var(--pp-border)" }}
              data-testid="pluckonomics-overlay-card"
            >
              <div
                className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full"
                style={{ backgroundColor: "rgba(87, 75, 140, 0.15)" }}
                aria-hidden="true"
              >
                <Sparkles className="h-6 w-6" style={{ color: "#574B8C" }} />
              </div>
              <h2
                className="mb-2 font-heading text-2xl font-bold text-pp-text"
                data-testid="pluckonomics-overlay-title"
              >
                See your full Pluckonomics
              </h2>
              <p
                className="mb-4 text-sm text-muted-foreground"
                data-testid="pluckonomics-overlay-subtitle"
              >
                {trialUsedUp
                  ? "Standard preview period has ended. Available with Preferred & Elite plans."
                  : `Available with ${targetTierLabel === "Preferred" ? "Preferred & Elite plans" : "Elite plan"}.`}
              </p>
              {!trialUsedUp ? (
                interestRecorded ? (
                  <div
                    className="rounded-2xl border border-pp-border bg-pp-surface-soft px-5 py-4 text-sm text-pp-text"
                    data-testid="pluckonomics-interest-recorded"
                  >
                    Saved — we&rsquo;ll email you before billing
                    launches. You&rsquo;ll confirm your plan then;
                    nothing is charged or auto-started.
                  </div>
                ) : (
                  <>
                    {/* Affirmative price disclosure — MUST appear
                        BEFORE the intent-capture button is
                        actionable. Amendment Requirement 1
                        (2026-07-24). Missing price → the button
                        below is disabled + the drift-guard test
                        `pluckonomics-price-before-intent` fails. */}
                    {targetTierPriceDollars ? (
                      <p
                        className="mb-4 text-sm font-medium text-pp-text"
                        data-testid="pluckonomics-price-disclosure"
                      >
                        {targetTierLabel} &mdash; ${targetTierPriceDollars}/mo
                        when billing launches.{" "}
                        <span className="text-muted-foreground">
                          No charge today; nothing starts automatically.
                        </span>
                      </p>
                    ) : (
                      <p
                        className="mb-4 text-sm text-muted-foreground"
                        data-testid="pluckonomics-price-loading"
                      >
                        Pricing loading&hellip;
                      </p>
                    )}
                    <Button
                      onClick={() => onSaveMySpot(targetTier)}
                      disabled={startingTrial || !targetTierPriceDollars}
                      className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
                      data-testid="pluckonomics-save-my-spot"
                    >
                      {startingTrial ? "Saving\u2026" : "Save my spot"}
                    </Button>
                  </>
                )
              ) : (
                <Button
                  disabled
                  className="rounded-full"
                  data-testid="pluckonomics-trial-used"
                >
                  Preview used
                </Button>
              )}
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
